import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { useSWRFetch } from '@hooks/useSWRFetch';
import { FETCH_STATUS } from '@hooks/useSWRFetch/types';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import {
  DEFAULT_LIMIT,
  DEFAULT_PAGE,
  GA_PRODUCT_CATALOG,
  SEARCH_SORT_DIRECTION_DESC,
} from '@helpers/constants';
import { queryStateFromSearchParams } from '@helpers/queryStateFromSearchParams';
import {
  getBase62FromUrlOrUri,
  getSearchType,
  isEmptyString,
} from '@helpers/utils';
import { _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output } from '@generated/grpc/spotify/s4mpview/v1';
import { getSearchUrl } from './helpers';
import {
  SearchedAlbumsResponse,
  SearchParams,
  SearchSortDirection,
  SearchSortField,
  SearchType,
} from './types';

const ORIGINAL_RELEASE_DATE = 'ORIGINAL_RELEASE_DATE';

export function useSearchedAlbums(
  userFeeds:
    | _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output[]
    | undefined,
  userLicensors:
    | _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output[]
    | undefined,
) {
  const router = useRouter();
  const [status, setStatus] = useState<FETCH_STATUS>(FETCH_STATUS.IDLE);
  const [searchParams, setSearchParams] = useState<SearchParams>({
    searchType: SearchType.FREE_TEXT,
    searchTerm: '',
    feedIds: [],
    licensorIds: [],
    limit: DEFAULT_LIMIT,
    searchSortField: ORIGINAL_RELEASE_DATE,
    searchSortDirection: SEARCH_SORT_DIRECTION_DESC,
    page: DEFAULT_PAGE,
  });

  const TWO_MINUTES_IN_MS = 120000;

  const requestObject = {
    url: getSearchUrl(searchParams),
  };

  const canSearch =
    !isEmptyString(searchParams.searchTerm) &&
    // The endpoint currently does not support search by URI and we handle this case strictly on the FE
    (searchParams.searchType === SearchType.FREE_TEXT ||
      searchParams.searchType === SearchType.BULK_SEARCH);

  const {
    data: fetchedData,
    status: apiFetchStatus,
    isLoading,
  } = useSWRFetch<SearchedAlbumsResponse>(canSearch ? requestObject : null, {
    dedupingInterval: TWO_MINUTES_IN_MS,
  });

  useEffect(() => {
    const searchResults = fetchedData?.searchResults;

    if (!!apiFetchStatus && apiFetchStatus !== 200) {
      setStatus(FETCH_STATUS.ERROR);
      return;
    }

    if (isLoading) {
      setStatus(FETCH_STATUS.FETCHING);
    } else if (
      (!searchResults || searchResults.length === 0) &&
      !searchParams.searchTerm.startsWith('00') &&
      !isEmptyString(searchParams.searchTerm)
    ) {
      const {
        limit,
        page,
        searchSortField,
        searchSortDirection,
        ...remainingQueryParams
      } = router.query;

      router.push(
        {
          pathname: '/search',
          query: {
            ...remainingQueryParams,
            q: encodeURIComponent('00'.concat(searchParams.searchTerm)),
            limit: limit || DEFAULT_LIMIT,
            searchSortDirection: SEARCH_SORT_DIRECTION_DESC,
            searchSortField: ORIGINAL_RELEASE_DATE,
            page: DEFAULT_PAGE,
          },
        },
        undefined,
        { shallow: true },
      );
    } else if (!!searchResults && !isEmptyString(searchParams.searchTerm)) {
      setStatus(FETCH_STATUS.SUCCESS);
    }
  }, [
    searchParams,
    fetchedData?.searchResults,
    apiFetchStatus,
    isLoading,
    router,
  ]);

  /**
   * Main function used by the Search page to perform a search
   * Any time the 'q' query param is updated, this function will rerun and update the BE search url with the term.
   */

  const runSearchWithQueryParams = useCallback(() => {
    if (!userFeeds?.length && !userLicensors?.length) {
      return;
    }

    const {
      searchStateFromQueryParams,
      licensorStateFromQueryParams,
      feedStateFromQueryParams,
      limitStateFromQueryParams,
      pageStateFromQueryParams,
      searchSortDirectionFromQueryParams,
      searchSortFieldFromQueryParams,
    } = queryStateFromSearchParams(router.query);

    if (searchStateFromQueryParams.length === 0) {
      router.push({ pathname: '/catalog' });
    }

    const trimmedSearchTerm = searchStateFromQueryParams.join(',').trim();
    const searchType = getSearchType(trimmedSearchTerm);

    /**
     * We should reserve query state searches for free text/bulk terms that need to show a list of results.
     * URI/URL searches are FE only, do not return a list of results, and so do not need to be part of search page flow.
     * Allowing uri/url in the query state in the search page flow would cause a redirect loop
     * because upon landing on search?q={someUri}, the only option is to redirect to the release/track/artist page.
     * For example: clicking back from release > search?q={someUri}, would just lead to user
     * being immediately pushed back to the release page again, making the search page useless in this case.
     */
    if (
      searchType !== SearchType.FREE_TEXT &&
      searchType !== SearchType.BULK_SEARCH
    ) {
      router.push({ pathname: '/catalog' });
    }

    const licensorsFilteredByQueryParamIds: _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output[] =
      licensorStateFromQueryParams.length && userLicensors?.length
        ? userLicensors.filter(licensor =>
            licensorStateFromQueryParams.includes(licensor.id),
          )
        : [];

    const feedsFilteredByQueryParamIds: _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output[] =
      feedStateFromQueryParams.length && userFeeds?.length
        ? userFeeds.filter(feed => feedStateFromQueryParams.includes(feed.id))
        : [];

    setSearchParams({
      searchType: searchType,
      searchTerm: trimmedSearchTerm,
      limit: limitStateFromQueryParams || DEFAULT_LIMIT,
      page: pageStateFromQueryParams || DEFAULT_PAGE,
      searchSortField: searchSortFieldFromQueryParams || ORIGINAL_RELEASE_DATE,
      searchSortDirection:
        searchSortDirectionFromQueryParams || SEARCH_SORT_DIRECTION_DESC,
      feedIds: feedsFilteredByQueryParamIds.map(
        (feed: _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output) =>
          feed.id,
      ),
      licensorIds: licensorsFilteredByQueryParamIds.map(
        (licensor: _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output) =>
          licensor.id,
      ),
    });

    sendEvent({
      eventCategory: EVENT_CATEGORY.SEARCH_MODULE,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: `Searched by ${searchType}`,
      product: GA_PRODUCT_CATALOG,
    });
  }, [userFeeds, userLicensors, router]);

  /**
   * This function is called when submitting a search from Catalog/Search pages.
   * Any FE specific searches (URI/URL for example) should be handled in here.
   * Free text searches that need to call the BE and show results will be passed to the Search page
   * with the term saved as a query param and the search will be handled by the `runSearchWithQueryParams` function.
   */

  const onSearchSubmit = (inputValue: string) => {
    const trimmedTerm = inputValue.trim();
    const searchType = getSearchType(trimmedTerm);

    let searchTerm = inputValue;
    if (
      searchType !== SearchType.FREE_TEXT &&
      searchType !== SearchType.BULK_SEARCH &&
      searchType !== SearchType.TRACK_URL
    ) {
      switch (searchType) {
        case SearchType.ALBUM_URI:
        case SearchType.ALBUM_URL:
          router.push(`/release/${getBase62FromUrlOrUri(trimmedTerm)}`);
          break;
        case SearchType.ARTIST_URI:
        case SearchType.ARTIST_URL:
          router.push(`/artist/${getBase62FromUrlOrUri(trimmedTerm)}`);
          break;
        default:
          break;
      }

      sendEvent({
        eventCategory: EVENT_CATEGORY.SEARCH_MODULE,
        eventAction: EVENT_ACTION.CLICK,
        eventLabel: `Searched by ${searchType}`,
        product: GA_PRODUCT_CATALOG,
      });
      return;
    }

    if (searchType === SearchType.TRACK_URL) {
      searchTerm = `spotify:track:${getBase62FromUrlOrUri(trimmedTerm)}`;
    }

    const {
      tab,
      limit,
      page,
      searchSortField,
      searchSortDirection,
      ...remainingQueryParams
    } = router.query;

    router.push(
      {
        pathname: '/search',
        query: {
          ...remainingQueryParams,
          q: encodeURIComponent(searchTerm),
          limit: limit || DEFAULT_LIMIT,
          searchSortDirection: SEARCH_SORT_DIRECTION_DESC,
          searchSortField: ORIGINAL_RELEASE_DATE,
          page: DEFAULT_PAGE,
        },
      },
      undefined,
      { shallow: true },
    );
  };

  const onFeedFilterSelection = (
    inputValue: string,
    selectedFeedIds: string[],
  ) => {
    const {
      searchSortDirection,
      searchSortField,
      feeds,
      limit,
      page,
      ...updatedQuery
    } = router.query;

    router.push(
      {
        pathname: '/search',
        query: {
          ...updatedQuery,
          q: encodeURIComponent(inputValue),
          ...(selectedFeedIds.length > 0 && {
            feeds: selectedFeedIds.join(','),
          }),
          limit: limit || DEFAULT_LIMIT,
          page: DEFAULT_PAGE,
          searchSortDirection:
            searchSortDirection || SEARCH_SORT_DIRECTION_DESC,
          searchSortField: searchSortField || ORIGINAL_RELEASE_DATE,
        },
      },
      undefined,
      { shallow: true },
    );
  };

  const onLicensorFilterSelection = (
    inputValue: string,
    selectedLicensorIds: string[],
  ) => {
    const {
      searchSortDirection,
      searchSortField,
      licensors,
      limit,
      page,
      ...updatedQuery
    } = router.query;

    router.push(
      {
        pathname: '/search',
        query: {
          ...updatedQuery,
          q: encodeURIComponent(inputValue),
          ...(selectedLicensorIds.length > 0 && {
            licensors: selectedLicensorIds.join(','),
          }),
          limit: limit || DEFAULT_LIMIT,
          page: DEFAULT_PAGE,
          searchSortDirection:
            searchSortDirection || SEARCH_SORT_DIRECTION_DESC,
          searchSortField: searchSortField || ORIGINAL_RELEASE_DATE,
        },
      },
      undefined,
      { shallow: true },
    );
  };

  const onLimitChange = useCallback(
    (updatedLimit: number) => {
      const {
        searchSortDirection,
        searchSortField,
        limit,
        page,
        ...updatedQuery
      } = router.query;

      router.push(
        {
          pathname: '/search',
          query: {
            ...updatedQuery,
            limit: updatedLimit,
            page: DEFAULT_PAGE,
            searchSortDirection:
              searchSortDirection || SEARCH_SORT_DIRECTION_DESC,
            searchSortField: searchSortField || ORIGINAL_RELEASE_DATE,
          },
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  const onPagination = useCallback(
    (updatedPage: number) => {
      const {
        searchSortField,
        searchSortDirection,
        page,
        limit,
        ...updatedQuery
      } = router.query;

      router.push(
        {
          pathname: '/search',
          query: {
            ...updatedQuery,
            limit: limit || DEFAULT_LIMIT,
            page: updatedPage,
            searchSortDirection:
              searchSortDirection || SEARCH_SORT_DIRECTION_DESC,
            searchSortField: searchSortField || ORIGINAL_RELEASE_DATE,
          },
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  const onSortChange = useCallback(
    (fieldName: SearchSortField, sortDirection: SearchSortDirection) => {
      const { searchSortField, searchSortDirection, ...updatedQuery } =
        router.query;

      router.push(
        {
          pathname: '/search',
          query: {
            ...updatedQuery,
            searchSortField: fieldName,
            searchSortDirection: sortDirection,
          },
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  return {
    data: {
      searchResults: fetchedData?.searchResults,
    },
    actions: {
      runSearchWithQueryParams,
      onSearchSubmit,
      onFeedFilterSelection,
      onLicensorFilterSelection,
      onLimitChange,
      onPagination,
      onSortChange,
    },
    status,
  };
}
